import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { GrSteps } from '../../components/icons/GrSteps';
import { Loader2, MoreVertical, Printer, RefreshCw, Trash2, Undo2 } from 'lucide-react';

import {
  PasAnnotationWorkbench,
  type PasAnnotationWorkbenchHandle,
} from '../../components/pas/PasAnnotationWorkbench';
import {
  RevisionPrintPreviewModal,
  useRevisionPrintAction,
  type RevisionPrintParticle,
} from '../review/print';
import type { Asset, KnowledgeParticle } from '../../types/domain';
import { getParticleTitle } from '../../utils/particleDisplay';
import { readPasAnchorFromAssetMetadata, readPasAnchorPracticeItems, readPasWorkbenchRegionsFromAssetMetadata, summarizePasPracticeLinksByThemeKp } from '../../utils/pasAsset';
import { refreshPasPracticeLibrary, type PasPracticeLibraryItem } from '../../utils/pasPracticeLibrary';
import { resolveSubjectCodeFromCandidates } from '../../utils/subjectConfig';
import { AssetFittedGraphView } from './AssetFittedGraphView';
import {
  buildAssetWorkspacePath,
  parseAssetWorkspaceView,
} from './assetWorkspaceRouting';
import { buildLadderPracticePrintParticles } from './buildLadderPracticePrintParticles';
import { WorkspaceGraphView } from './WorkspaceGraphView';

export type AssetGraphViewMode = 'fitted' | 'related' | 'practice';

type AssetWorkspaceViewsProps = {
  assetId: string;
  asset: Asset;
  particles: KnowledgeParticle[];
  onAssetUpdated?: (asset: Asset) => void;
  onToast?: (message: string) => void;
  className?: string;
  /** When omitted, read from `/assets/:id/:workspaceView`. */
  workspaceView?: AssetGraphViewMode;
};

const EMPTY_PRINT_PARTICLES: RevisionPrintParticle[] = [];

const VIEW_TABS: Array<{ id: AssetGraphViewMode; label: string }> = [
  { id: 'fitted', label: '适配屏幕' },
  { id: 'related', label: '关系视图' },
  { id: 'practice', label: '阶梯练习视图' },
];

function collectPracticeLibraryItemsFromAsset(asset: Asset): PasPracticeLibraryItem[] {
  const metadata = asset.metadata && typeof asset.metadata === 'object' && !Array.isArray(asset.metadata)
    ? asset.metadata as Record<string, unknown>
    : null;
  const seen = new Set<string>();
  const items: PasPracticeLibraryItem[] = [];
  for (const item of refreshPasPracticeLibrary(readPasAnchorPracticeItems(metadata))) {
    const id = String(item.id || '').trim();
    if (!id || seen.has(id)) continue;
    seen.add(id);
    items.push(item);
  }
  for (const region of readPasWorkbenchRegionsFromAssetMetadata(metadata)) {
    for (const item of refreshPasPracticeLibrary(region.practiceItems || [])) {
      const id = String(item.id || '').trim();
      if (!id || seen.has(id)) continue;
      seen.add(id);
      items.push(item);
    }
  }
  return items;
}

export function AssetWorkspaceViews({
  assetId,
  asset,
  particles,
  onAssetUpdated,
  onToast,
  className = '',
  workspaceView: workspaceViewProp,
}: AssetWorkspaceViewsProps) {
  const navigate = useNavigate();
  const { workspaceView: workspaceViewParam } = useParams();
  const [searchParams] = useSearchParams();
  const viewMode = workspaceViewProp ?? parseAssetWorkspaceView(workspaceViewParam);
  const [pasSelectMode, setPasSelectMode] = useState(false);
  const [pasCanUndo, setPasCanUndo] = useState(false);
  const [pasRefreshing, setPasRefreshing] = useState(false);
  const [practiceMoreOpen, setPracticeMoreOpen] = useState(false);
  const practiceMoreRef = useRef<HTMLDivElement>(null);
  const [printParticles, setPrintParticles] = useState<RevisionPrintParticle[]>(EMPTY_PRINT_PARTICLES);
  const [pendingOpenPrint, setPendingOpenPrint] = useState(false);
  const pasWorkbenchRef = useRef<PasAnnotationWorkbenchHandle>(null);

  const subjectCode = useMemo(
    () => resolveSubjectCodeFromCandidates(asset.subject, asset.ai_category) ?? 'math',
    [asset.ai_category, asset.subject],
  );

  const pasAnchor = useMemo(
    () => readPasAnchorFromAssetMetadata(
      asset.metadata && typeof asset.metadata === 'object' && !Array.isArray(asset.metadata)
        ? asset.metadata as Record<string, unknown>
        : null,
    ),
    [asset.metadata],
  );

  const printSessionKey = pasAnchor?.sessionId || assetId;

  const {
    openPreview: openPrintPreview,
    closePreview: closePrintPreview,
    previewVisible: printPreviewVisible,
    printing: printPending,
    sharing: sharePending,
    print: runPrint,
    share: runSharePrintPdf,
    printSessionId,
    resolvedParticles: previewParticles,
    resolvingEmbeddedImages,
  } = useRevisionPrintAction({
    particles: printParticles,
    sessionId: printSessionKey,
    sheetKind: 'pas',
    onNotice: onToast,
  });

  useEffect(() => {
    if (!pendingOpenPrint) return;
    if (printParticles.length === 0) return;
    setPendingOpenPrint(false);
    openPrintPreview();
  }, [openPrintPreview, pendingOpenPrint, printParticles]);

  const handlePracticeRefresh = useCallback(() => {
    if (pasRefreshing) return;
    setPasRefreshing(true);
    void (async () => {
      try {
        await pasWorkbenchRef.current?.reload();
        onToast?.('已刷新标注');
      } catch {
        onToast?.('刷新失败');
      } finally {
        setPasRefreshing(false);
      }
    })();
  }, [onToast, pasRefreshing]);

  useEffect(() => {
    if (!practiceMoreOpen) return undefined;
    const handlePointerDown = (event: MouseEvent) => {
      if (!practiceMoreRef.current?.contains(event.target as Node)) {
        setPracticeMoreOpen(false);
      }
    };
    document.addEventListener('mousedown', handlePointerDown);
    return () => document.removeEventListener('mousedown', handlePointerDown);
  }, [practiceMoreOpen]);

  const handleClearLadderPractice = useCallback(() => {
    const count = pasWorkbenchRef.current?.listPracticeLibraryItems().length ?? 0;
    if (count === 0) {
      onToast?.('当前没有阶梯练习题目可清除');
      return;
    }
    const ok = window.confirm(
      '将清除本答题纸上当前阶梯练习题目库（含起手式与各框选组），框选区域会保留。确定继续？',
    );
    if (!ok) return;
    void pasWorkbenchRef.current?.clearLadderPracticeLibrary();
  }, [onToast]);

  const handlePracticePrint = useCallback(() => {
    const fromWorkbench = pasWorkbenchRef.current?.listPracticeLibraryItems() || [];
    const libraryItems = fromWorkbench.length > 0
      ? fromWorkbench
      : collectPracticeLibraryItemsFromAsset(asset);
    const next = buildLadderPracticePrintParticles({
      particles,
      libraryItems,
      subject: subjectCode,
    });
    if (next.length === 0) {
      onToast?.('练习库暂无可打印的阶梯题，先加入后再打印。');
      return;
    }
    setPrintParticles(next);
    setPendingOpenPrint(true);
  }, [asset, onToast, particles, subjectCode]);

  const practiceThemeLinks = useMemo(
    () => summarizePasPracticeLinksByThemeKp(
      asset.metadata && typeof asset.metadata === 'object' && !Array.isArray(asset.metadata)
        ? asset.metadata as Record<string, unknown>
        : null,
    ),
    [asset.metadata],
  );

  const themeParticles = useMemo(
    () => particles.map((particle) => ({
      id: particle.id,
      title: getParticleTitle(particle),
      summary: String(particle.questions || '').slice(0, 120) || undefined,
    })),
    [particles],
  );

  const focusRegionId = searchParams.get('regionId');

  const handleActiveRegionChange = useCallback((regionId: string | null) => {
    if (viewMode !== 'practice') return;
    const current = searchParams.get('regionId');
    const next = String(regionId || '').trim();
    if ((current || '') === next) return;
    navigate(buildAssetWorkspacePath(assetId, 'practice', { regionId: next || null }), { replace: true });
  }, [assetId, navigate, searchParams, viewMode]);

  const openPracticeForParticle = useCallback((particleId: string) => {
    const link = practiceThemeLinks.get(particleId);
    const nextFocus = link?.regionIds?.[0] || null;
    navigate(buildAssetWorkspacePath(assetId, 'practice', { regionId: nextFocus }));
    setPasSelectMode(false);
    if (nextFocus) {
      requestAnimationFrame(() => {
        pasWorkbenchRef.current?.focusRegion(nextFocus);
      });
    }
  }, [assetId, navigate, practiceThemeLinks]);

  const handleSwitchView = useCallback((mode: AssetGraphViewMode) => {
    const nextSearch = mode === 'practice' && focusRegionId
      ? { regionId: focusRegionId }
      : undefined;
    navigate(buildAssetWorkspacePath(assetId, mode, nextSearch), { replace: true });
    setPasSelectMode(false);
  }, [assetId, focusRegionId, navigate]);

  return (
    <div className={`space-y-3 ${className}`}>
      <div className="flex flex-wrap items-center justify-between gap-2">
        <div className="inline-flex rounded-xl border border-border bg-surface p-1">
          {VIEW_TABS.map((tab) => {
            const active = viewMode === tab.id;
            return (
              <button
                key={tab.id}
                type="button"
                onClick={() => handleSwitchView(tab.id)}
                className={
                  'inline-flex items-center gap-1.5 rounded-lg px-3 py-1.5 text-sm transition '
                  + (active
                    ? 'bg-primary text-white shadow-sm'
                    : 'text-muted hover:bg-canvas hover:text-ink')
                }
              >
                {tab.id === 'practice' ? (
                  <GrSteps size={14} className="shrink-0" />
                ) : null}
                {tab.label}
              </button>
            );
          })}
        </div>

        {viewMode === 'practice' ? (
          <div className="flex flex-wrap items-center gap-2">
            <button
              type="button"
              disabled={!pasCanUndo}
              onClick={() => pasWorkbenchRef.current?.undo()}
              className="inline-flex items-center gap-1.5 rounded-lg border border-border bg-surface px-3 py-1.5 text-sm text-ink disabled:opacity-40"
            >
              <Undo2 size={16} />
              撤销
            </button>
            <button
              type="button"
              disabled={pasRefreshing}
              onClick={handlePracticeRefresh}
              className="inline-flex items-center gap-1.5 rounded-lg border border-border bg-surface px-3 py-1.5 text-sm text-ink disabled:opacity-40"
            >
              {pasRefreshing ? <Loader2 size={16} className="animate-spin" /> : <RefreshCw size={16} />}
              刷新
            </button>
            <button
              type="button"
              onClick={handlePracticePrint}
              className="inline-flex items-center gap-1.5 rounded-lg border border-border bg-surface px-3 py-1.5 text-sm text-ink"
              title="打印阶梯练习答题纸"
            >
              <Printer size={16} />
              打印
            </button>
            <button
              type="button"
              onClick={() => setPasSelectMode((value) => !value)}
              className={
                'inline-flex items-center gap-1.5 rounded-lg px-3 py-1.5 text-sm transition '
                + (pasSelectMode
                  ? 'bg-primary text-white'
                  : 'border border-border bg-surface text-ink')
              }
            >
              <GrSteps size={16} />
              {pasSelectMode ? '退出框选' : '铺台阶'}
            </button>
            <div ref={practiceMoreRef} className="relative">
              <button
                type="button"
                className="inline-flex h-[34px] w-[34px] items-center justify-center rounded-lg border border-border bg-surface text-ink hover:bg-black/[0.04]"
                aria-label="阶梯练习更多操作"
                aria-expanded={practiceMoreOpen}
                onClick={() => setPracticeMoreOpen((open) => !open)}
              >
                <MoreVertical size={16} />
              </button>
              {practiceMoreOpen ? (
                <div className="absolute right-0 top-[calc(100%+4px)] z-20 min-w-[180px] overflow-hidden rounded-lg border border-border bg-surface py-1 shadow-lg">
                  <button
                    type="button"
                    className="flex w-full items-center gap-2 px-3 py-2 text-left text-sm text-danger hover:bg-danger/5"
                    onClick={() => {
                      setPracticeMoreOpen(false);
                      handleClearLadderPractice();
                    }}
                  >
                    <Trash2 size={14} />
                    清除当前阶梯视图
                  </button>
                </div>
              ) : null}
            </div>
          </div>
        ) : null}
      </div>

      {viewMode === 'fitted' ? (
        <AssetFittedGraphView
          assetId={assetId}
          asset={asset}
          practiceThemeLinks={practiceThemeLinks}
          onOpenPractice={openPracticeForParticle}
          onToast={onToast}
        />
      ) : null}

      {viewMode === 'related' ? (
        <WorkspaceGraphView assetId={assetId} />
      ) : null}

      {viewMode === 'practice' ? (
        <div className="overflow-hidden rounded-2xl border border-border bg-surface">
          <div className="relative min-h-[480px] h-[calc(100dvh-15rem)]">
            <div className="absolute inset-0">
              <PasAnnotationWorkbench
                ref={pasWorkbenchRef}
                asset={asset}
                assetId={assetId}
                enabled
                selectMode={pasSelectMode}
                themeParticles={themeParticles}
                initialFocusRegionId={focusRegionId}
                initialPracticeOpen={viewMode === 'practice'}
                onActiveRegionChange={handleActiveRegionChange}
                onCanUndoChange={setPasCanUndo}
                onToast={onToast}
                onAssetUpdated={onAssetUpdated}
              />
            </div>
          </div>
        </div>
      ) : null}

      <RevisionPrintPreviewModal
        visible={printPreviewVisible}
        particles={previewParticles}
        resolvingEmbeddedImages={resolvingEmbeddedImages}
        sessionId={printSessionKey}
        printSessionId={printSessionId}
        sheetKind="pas"
        previewTitle="打印阶梯练习答题纸"
        printing={printPending}
        sharing={sharePending}
        onClose={closePrintPreview}
        onPrint={() => void runPrint()}
        onShare={() => void runSharePrintPdf()}
      />
    </div>
  );
}
